import { InitOptions, MetadataValue, ServerTheme, UserMetadata } from '../types';
import { getTranslations, Translations } from '../utils/translations';

/**
 * CSS overrides for the SDK's dark color scheme. Applied as a block so light
 * mode simply omits them and the stylesheet's light defaults win.
 */
const DARK_MODE_VARS: Record<string, string> = {
    '--pr-bg-primary': '#1a202c',
    '--pr-bg-secondary': '#2d3748',
    '--pr-text-primary': '#f7fafc',
    '--pr-text-secondary': '#a0aec0',
    '--pr-border-color': '#4a5568',
    '--pr-overlay-bg': 'rgba(0, 0, 0, 0.7)',
    // Soft accent surface (e.g. the toast icon tile): a light accent tint reads
    // badly on dark, so use a subtle translucent chip instead. The icon stroke
    // still uses --pr-primary-color so the accent stays visible.
    '--pr-primary-light': 'rgba(255, 255, 255, 0.08)',
};

export class Config {
    public apiKey: string;
    public apiUrl: string;
    public user?: { id?: string; email?: string };
    public language: string;
    public platform: 'ios' | 'android' | 'web' | 'desktop' | 'undefined' | 'mobile';
    public country?: string;
    public debug: boolean;
    // How often (ms) to poll for new/updated announcements so single-page apps
    // pick up changes without a full reload. 0 disables polling. Defaults to 2
    // minutes; clamped to a sane floor so a misconfiguration can't hammer the API.
    public pollInterval: number;
    // Preview mode bypasses the "first visit" and "already read" personalization
    // on the backend so a developer sees every published announcement while
    // verifying their integration. Enabled by the `preview` init option or by a
    // `?pulserelease-preview` (or `?pulserelease_preview`) URL query flag.
    public preview: boolean;
    // Custom key/value metadata for the current visitor. Seeded from
    // `options.metadata` and `options.user?.metadata` (the latter wins on a
    // key collision), then run through the same clamp/drop validation as
    // setMetadata() so a bad init() call can't exceed the limits either.
    public metadata: UserMetadata;
    // Read-only, session-less guest mode. When true the SDK creates no visitor
    // identity or client-side state, runs no polling/trackers, never auto-renders
    // a boost, and serves only the on-demand announcement list. See
    // InitOptions.guest for the full contract.
    public guest: boolean;
    // HMAC identity-verification proof for the identified user, forwarded to the
    // server as `user_hash`. Only meaningful when the application requires
    // identity verification; ignored server-side otherwise. Computed by the
    // customer's backend (never the browser) — see UserInfo.hash.
    public hash?: string;
    private _translations: Translations;

    /**
     * Valid metadata key shape, mirrored 1:1 from the server's
     * `SdkCustomMetadata::KEY_PATTERN`: must START WITH A LETTER, then letters,
     * digits, dot, or hyphen — no underscore. An invalid key isn't dropped
     * outright: {@link stripKey} removes the disallowed characters and the
     * remainder is used, so both sides derive the same stored key and a queued
     * `DELETE /metadata/{key}` always carries a route-valid key.
     */
    public static readonly METADATA_KEY_PATTERN = /^[A-Za-z][A-Za-z0-9.-]*$/;
    /** Hard cap on the number of metadata keys; the server enforces the same limit. */
    public static readonly MAX_METADATA_KEYS = 50;
    /** Max metadata key length in characters; entries over this are dropped. */
    public static readonly MAX_METADATA_KEY_LENGTH = 100;
    /** Max metadata value length in code points; longer string values are dropped, matching the server. */
    public static readonly MAX_METADATA_VALUE_LENGTH = 255;

    constructor(options: InitOptions) {
        this.apiKey = options.apiKey;
        this.apiUrl = options.apiUrl || 'https://pulserelease.com/api/sdk';
        // Keep only the identity fields: metadata and hash live in their
        // own dedicated properties below, not inside the user context.
        this.user = options.user ? { id: options.user.id, email: options.user.email } : undefined;
        this.hash = options.user?.hash;
        this.language = options.language || this.detectLanguage();
        this.platform = options.platform || this.detectPlatform();
        this.country = options.country;
        this.debug = options.debug || false;
        this.pollInterval = Config.resolvePollInterval(options.pollInterval);
        this.preview =
            typeof options.preview === 'boolean' ? options.preview : this.detectPreviewFlag();
        this.guest = options.guest === true;
        this._translations = getTranslations(this.language);

        const rawMetadata: Record<string, unknown> = {
            ...options.metadata,
            ...options.user?.metadata,
        };
        const { accepted, ignored } = Config.sanitizeMetadata(rawMetadata);
        this.metadata = accepted;
        if (ignored.length > 0) {
            this.log('Dropped invalid metadata keys:', ignored);
        }

        this.validate();

        // Log configuration when debug is enabled
        this.log('Config initialized with API URL:', this.apiUrl);
    }

    /**
     * Normalize the poll interval: default 2 minutes, 0 (or negative) disables
     * polling, and any positive value is floored at 30s so a typo can't turn the
     * SDK into a request storm. Non-finite input (NaN/Infinity, e.g. from a bad
     * parseInt) falls back to the default rather than slipping past the floor.
     */
    private static resolvePollInterval(value?: number): number {
        if (value === undefined || !Number.isFinite(value)) {
            return 120000; // 2 minutes
        }

        if (value <= 0) {
            return 0;
        }

        return Math.max(30000, value);
    }

    /**
     * Normalize a raw key into one that satisfies {@link METADATA_KEY_PATTERN},
     * or null when nothing usable remains. Shared verbatim with the server's
     * `SdkCustomMetadata::stripKey` so both sides derive the same stored key:
     *
     *   1. Remove every character outside `[A-Za-z0-9.-]` (underscores, spaces,
     *      symbols, unicode, ...).
     *   2. Drop leading characters until the first is a letter (removes leading
     *      digits, dots, and hyphens).
     *   3. Return null when the result is empty or, in Unicode code points,
     *      longer than {@link MAX_METADATA_KEY_LENGTH} — the key is not truncated.
     *
     * Examples: `plan_tier` → `plantier`, `company.size` → `company.size`,
     * `2024cohort` → `cohort`, `___` → null.
     */
    public static stripKey(raw: string): string | null {
        const stripped = String(raw)
            .replace(/[^A-Za-z0-9.-]/g, '')
            .replace(/^[^A-Za-z]+/, '');

        if (stripped === '' || [...stripped].length > Config.MAX_METADATA_KEY_LENGTH) {
            return null;
        }

        return stripped;
    }

    /**
     * Validate and coerce a raw metadata object exactly as the server does:
     * booleans coerce to "true"/"false"; any other non-string/number value
     * (object, array, null, NaN, Infinity, ...) is dropped; each key is run
     * through {@link stripKey} — invalid characters are removed and the entry is
     * stored under the remainder, or dropped when nothing usable (or a key
     * longer than {@link MAX_METADATA_KEY_LENGTH}) remains; string values longer
     * than {@link MAX_METADATA_VALUE_LENGTH} are dropped (not truncated); and only
     * the first {@link MAX_METADATA_KEYS} otherwise-valid entries (in insertion
     * order) survive the hard cap. Length is measured in Unicode code points
     * (like the server's `mb_strlen`), not UTF-16 units, so multibyte values
     * agree. Clamps/drops rather than throwing, so a bad `setMetadata()`/
     * `init()` call from the host page can never break init.
     */
    public static sanitizeMetadata(input?: Record<string, unknown>): {
        accepted: UserMetadata;
        ignored: string[];
    } {
        const accepted: UserMetadata = {};
        const ignored: string[] = [];

        if (!input) {
            return { accepted, ignored };
        }

        for (const [key, rawValue] of Object.entries(input)) {
            // Strip invalid characters and store under the remainder (matching
            // the server's stripKey), so a queued DELETE /metadata/{key} can
            // never 404 and wedge the retry queue. Nothing usable → dropped.
            const strippedKey = Config.stripKey(key);
            if (strippedKey === null) {
                ignored.push(key);
                continue;
            }

            let value: MetadataValue;

            if (typeof rawValue === 'boolean') {
                value = rawValue ? 'true' : 'false';
            } else if (typeof rawValue === 'string') {
                value = rawValue;
            } else if (typeof rawValue === 'number' && Number.isFinite(rawValue)) {
                value = rawValue;
            } else {
                ignored.push(key);
                continue;
            }

            // Over-length string values are DROPPED (not truncated), matching
            // the server's SdkCustomMetadata::normalizeValue contract.
            if (typeof value === 'string' && [...value].length > Config.MAX_METADATA_VALUE_LENGTH) {
                ignored.push(key);
                continue;
            }

            if (Object.keys(accepted).length >= Config.MAX_METADATA_KEYS) {
                ignored.push(key);
                continue;
            }

            accepted[strippedKey] = value;
        }

        return { accepted, ignored };
    }

    /**
     * Build the CSS custom-property overrides for the server-provided
     * application theme: accent color (+ derived hover/light variants), corner
     * style (border-radius) and color mode (light/dark, with 'auto' following
     * the OS preference). Returns an empty object when no theme is provided so
     * the stylesheet defaults win.
     */
    public getServerThemeVariables(theme?: ServerTheme): Record<string, string> {
        const vars: Record<string, string> = {};

        if (!theme) {
            return vars;
        }

        if (theme.primary_color) {
            vars['--pr-primary-color'] = theme.primary_color;
            vars['--pr-primary-hover'] = Config.shadeColor(theme.primary_color, -0.15);
            vars['--pr-primary-light'] = Config.shadeColor(theme.primary_color, 0.85);
        }

        if (theme.corner_style) {
            vars['--pr-border-radius'] = theme.corner_style === 'square' ? '0px' : '12px';
        }

        if (this.resolveDarkMode(theme.color_mode)) {
            Object.assign(vars, DARK_MODE_VARS);
        }

        return vars;
    }

    /**
     * Whether the dark scheme should apply for the given color mode. 'auto'
     * follows the OS preference via matchMedia when available.
     */
    private resolveDarkMode(mode?: 'light' | 'dark' | 'auto'): boolean {
        if (mode === 'dark') {
            return true;
        }

        if (
            mode === 'auto' &&
            typeof window !== 'undefined' &&
            typeof window.matchMedia === 'function'
        ) {
            return window.matchMedia('(prefers-color-scheme: dark)').matches;
        }

        return false;
    }

    /**
     * Lighten (percent > 0) or darken (percent < 0) a hex color by the given
     * fraction. Non-hex inputs are returned unchanged so a malformed accent
     * never breaks rendering.
     */
    private static shadeColor(hex: string, percent: number): string {
        const match = /^#?([0-9a-f]{6})$/i.exec(hex.trim());
        if (!match) {
            return hex;
        }

        const int = parseInt(match[1], 16);
        const shade = (channel: number): number => {
            const target = percent < 0 ? 0 : 255;
            const amount = Math.abs(percent);
            return Math.round(channel + (target - channel) * amount);
        };

        const r = shade((int >> 16) & 0xff);
        const g = shade((int >> 8) & 0xff);
        const b = shade(int & 0xff);

        return '#' + [r, g, b].map((c) => c.toString(16).padStart(2, '0')).join('');
    }

    /**
     * Get translations for the configured language
     */
    public get t(): Translations {
        return this._translations;
    }

    private validate(): void {
        if (!this.apiKey) {
            throw new Error('PulseRelease: apiKey is required');
        }

        if (!this.apiKey.startsWith('pr_')) {
            throw new Error('PulseRelease: invalid API key format');
        }
    }

    /**
     * Detect the preview flag from the current page URL. Preview turns on when
     * the query string carries `pulserelease-preview` or `pulserelease_preview`
     * with a truthy or empty value (bare presence counts, e.g.
     * `?pulserelease-preview`, `=1`, `=true`); an explicit falsy value
     * (`=0`/`=false`) leaves it off. Guarded for non-browser environments.
     */
    private detectPreviewFlag(): boolean {
        if (typeof window === 'undefined' || !window.location) {
            return false;
        }

        try {
            const params = new URLSearchParams(window.location.search);

            for (const key of ['pulserelease-preview', 'pulserelease_preview']) {
                if (!params.has(key)) {
                    continue;
                }

                const value = (params.get(key) || '').trim().toLowerCase();

                // Bare presence (empty value) counts as on; otherwise only an
                // explicit falsy value keeps it off.
                if (!['0', 'false', 'no', 'off'].includes(value)) {
                    return true;
                }
            }
        } catch {
            // A malformed location must never break host-page init.
            return false;
        }

        return false;
    }

    private detectLanguage(): string {
        if (typeof navigator !== 'undefined') {
            return navigator.language.split('-')[0] || 'en';
        }
        return 'en';
    }

    private detectPlatform(): 'ios' | 'android' | 'web' | 'desktop' | 'mobile' | 'undefined' {
        if (typeof window === 'undefined') {
            return 'desktop';
        }

        const userAgent = navigator.userAgent.toLowerCase();

        // Check for iOS devices
        if (/iphone|ipad|ipod/i.test(userAgent)) {
            return 'ios';
        }

        // Check for Android devices
        if (/android/i.test(userAgent)) {
            return 'android';
        }

        // Check if running in Electron or similar desktop wrapper
        if (
            (window as any).electron ||
            /electron/i.test(userAgent) ||
            (navigator as any).userAgentData?.platform === 'desktop'
        ) {
            return 'desktop';
        }

        // Check for other mobile devices
        if (/mobile|blackberry|iemobile|opera mini/i.test(userAgent)) {
            return 'mobile';
        }

        // Default to web for desktop browsers
        return 'web';
    }

    public log(...args: unknown[]): void {
        if (this.debug) {
            console.log('[PulseRelease]', ...args);
        }
    }

    public error(...args: unknown[]): void {
        if (this.debug) {
            console.error('[PulseRelease]', ...args);
        }
    }

    /**
     * Always-on developer warning, independent of the `debug` option.
     *
     * `log()`/`error()` are opt-in diagnostics for someone actively debugging;
     * this is the floor every integrator sees even with `debug` off, reserved
     * for misconfiguration that otherwise fails completely silently (bad API
     * key, blocked origin, unreachable API — see PulseRelease.runInit). Pass a
     * single, fully-formed, "PulseRelease:"-prefixed message rather than a
     * varargs list, since callers rely on the exact string for greppability.
     */
    public warn(message: string): void {
        console.warn(message);
    }
}
